import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import qs from 'qs'
import type { PairOrderScheduleSearchReq } from '@/services/master/pairOrderSchedule/types'

dayjs.extend(customParseFormat)

/**
 * 메뉴 세션(`moms_menu_on_session`)·URL 쿼리에 싣는 조회 조건 모양. 다른 메뉴를 다녀와도 마지막 [조회]가 남는다(MOMS 공통 UX).
 * 저장은 erpInbound `searchQuery.ts` 와 같이 세션·URL 둘 다, 복원은 URL 에서 한다.
 *
 * 결제일 기간과 확정된 목록 검색어를 싣는다. 페이지는 싣지 않는다. 전량을 받아 화면이 나누므로 조회 조건이 아니다.
 */
export interface PairOrderScheduleSearchQuery {
  /** yyyyMMdd */
  fromClsDate?: string
  /** yyyyMMdd */
  toClsDate?: string
  /** 표 위 목록 검색어(받은 행을 거르는 클라이언트 필터) */
  keyword?: string
}

export const toSearchQuery = (req: PairOrderScheduleSearchReq, keyword = ''): PairOrderScheduleSearchQuery => ({
  fromClsDate: req.fromClsDate,
  toClsDate: req.toClsDate,
  ...(keyword ? { keyword } : {}),
})

const ymd = (value: unknown): string | undefined =>
  typeof value === 'string' && /^\d{8}$/.test(value) && dayjs(value, 'YYYYMMDD', true).isValid() ? value : undefined

export interface RestoredPairOrderScheduleSearch {
  /** 복원할 조회 조건. 없으면(최초 진입·조작된 URL) null 이고 자동 조회하지 않는다. */
  req: PairOrderScheduleSearchReq | null
  keyword: string
}

/**
 * 저장된 쿼리를 조회 조건으로 되돌린다. 날짜가 형식 밖이거나 시작일이 종료일보다 늦으면 복원하지 않는다.
 * 그때는 목록 검색어도 버린다. 조회 없이 검색어만 남기면 빈 표에 필터가 걸린 채 열린다.
 */
export const fromSearchQuery = (query: Partial<Record<keyof PairOrderScheduleSearchQuery, unknown>>): RestoredPairOrderScheduleSearch => {
  const fromClsDate = ymd(query.fromClsDate)
  const toClsDate = ymd(query.toClsDate)
  const req = fromClsDate && toClsDate && fromClsDate <= toClsDate ? { fromClsDate, toClsDate } : null
  return { req, keyword: req && typeof query.keyword === 'string' ? query.keyword : '' }
}

/**
 * 현재 주소의 쿼리를 읽어 복원한다. 생성본이 조회영역 초기값(filterInitial)을 표 슬롯보다 먼저, 훅 밖에서 읽으므로
 * `useParsedQuery` 와 같은 규칙(qs comma 파싱)으로 `window.location.search` 를 직접 읽는다.
 */
export const readSearchQuery = (search: string = window.location.search): RestoredPairOrderScheduleSearch =>
  fromSearchQuery(search ? qs.parse(search.slice(1), { comma: true, parseArrays: true }) : {})

/** 조회 조건(yyyyMMdd) → 조회영역 결제일 값(YYYY-MM-DD 쌍) */
export const toClsDateRange = (req: PairOrderScheduleSearchReq): [string, string] => [
  dayjs(req.fromClsDate, 'YYYYMMDD').format('YYYY-MM-DD'),
  dayjs(req.toClsDate, 'YYYYMMDD').format('YYYY-MM-DD'),
]
